import { MeasureDataGroup, MeasureDataRound } from "components/Measure-data/Groups-form/GroupsFormContext";
import {
  AltiusRes,
  CurrencyType,
  DateRange,
  GetSplitDetailsReq,
  IGetMonitoringData,
  IGetMonitoringDataRes,
  ILatestMeasureData,
  IMeasureData,
  IUpdateMeasureDataPayload,
  IUpdateMeasuresValues,
  Measure,
  MeasureCalcFieldsTable,
  MeasureDataDraft,
  MeasureDataSplitDetails,
  MeasureDataSummaryBatchTable,
  MeasureDataTableFilters,
  MeasureFee,
  MeasureFeesTable,
  MeasureGroup,
  MeasureParameter,
  MonitoringDataFiles,
  UpdateMeasureCalcFieldsPayload,
  UpdateMeasurePayload
} from "store/types";
import { formatDate } from "utils";
import downloadFileAxios from "utils/download-file-axios";
import { toastify } from "utils/tyostify";

import { DealAction } from "..";
import { AsyncActions, AsyncActionsParams } from "../base";

type DataFiltersToReq = {
  deal_ids?: number[];
  accountingDate?: DateRange<string>;
  effectiveDate?: DateRange<string>;
  createdAtDate?: DateRange<string>;
  updatedAtDate?: DateRange<string>;
  measures?: number[];
  investors?: number[];
  // rounds?: number[];
  enteredBy?: number[];
  calculatedFields?: string[]; // Should be calc field keys
  lastTransactions?: boolean;
  hideDeleted?: boolean;
};

export interface IMeasureActions {
  getMeasureDataTableAction: (data: IGetMonitoringData) => Promise<IGetMonitoringDataRes>;
  getOneMeasureDataAction: (id: number) => Promise<IMeasureData>;

  deleteMeasureDataAction: (id: number, deleteFullTransaction?: boolean) => Promise<void>;
  updateMeasureDataAction: (data: IUpdateMeasureDataPayload) => Promise<void>;
  splitCheckAction: (id: number) => Promise<boolean>;
  getOrganizationGraphAction: (id: number) => Promise<void>;

  saveMeasureDataDraftAction: (data: { deal_id: number; data: MeasureDataDraft["data"]; id?: number }) => Promise<void>;
  getMeasureDataDraftsAction: (deal_ids?: number[]) => Promise<MeasureDataDraft[]>;
  deleteMeasureDataDraftAction: (id: number) => Promise<void>;
  getMeasureDataDraftAction: (id: number) => Promise<
    {
      deal_title: string;
      deal_currency: CurrencyType;
    } & MeasureDataDraft
  >;

  setMeasureDataTableFiltersAction: (data: Partial<MeasureDataTableFilters> | null) => void;

  getSplitDetailsAction: (data: GetSplitDetailsReq) => Promise<MeasureDataSplitDetails>;

  downloadDataAction: (data: {
    deal_ids?: number[];
    filters: Partial<MeasureDataTableFilters>;
    hideInvestors: boolean;
  }) => Promise<void>;

  getMeasureDataCalcAction: (data: {
    round_id: number;
    split_type: "by_investor" | "by_commitments" | "by_parent_commitments";
    value: string;
    investor_id?: number;
    parent_deal_id?: number;
  }) => Promise<{
    round_id: number;
    investors: { id: number; value: string; name: string; investor_group: string | null }[];
  }>;

  getGroupedMeasureDataCalcAction: (data: {
    round_id: number;
    split_type: "entire_commitment" | "group_commitment";
    value: string;
    investor_ids: number[];
  }) => Promise<{
    round_id: number;
    investors: { id: number; value: string; name: string }[];
  }>;

  createManyMeasureDataAction: (data: {
    groups: MeasureDataGroup[];
    roundsByDeals: Record<number, MeasureDataRound[]>;
    edit_type?: "batch" | "split";
  }) => Promise<void>;

  getMeasureParametersAction: () => Promise<MeasureParameter[]>;
  getUsedMeasuresParametersAction: (deal_id?: number) => Promise<MeasureParameter[]>;
  getMeasuresDefinitionsListAction: () => Promise<void>;
  getMeasuresAction: () => Promise<Measure[]>;
  createMeasureAction: (data: Measure) => Promise<void>;
  updateMeasureAction: (data: UpdateMeasurePayload) => Promise<void>;
  deleteMeasureAction: (id: number) => Promise<void>;
  getMeasuresGroups: () => Promise<MeasureGroup[] | undefined>;
  getPrimeRoles: () => Promise<string[]>;
  getMeasureCalcFieldsTable: () => Promise<MeasureCalcFieldsTable>;
  updateMeasureCalcFieldsTable: (data: UpdateMeasureCalcFieldsPayload) => Promise<void>;
  updateMeasuresValuesAction: (data: IUpdateMeasuresValues) => Promise<void>;
  downloadBatchHistoryAction: (batch_id: number) => Promise<void>;
  getBatchTemplateAction: (data: { batch_id: number; template_id: number }) => Promise<void>;
  getLatestMeasureDataTableAction: (dealId: number) => Promise<ILatestMeasureData>;
  getMeasureFessTableAction: (dealId: number) => Promise<MeasureFeesTable>;
  createMeasureFeeAction: (data: {
    deal_id: number;
    source: "contract" | "report" | "previous-period";
    amount: string;
    from_date: string;
    to_date: string;
    override_existing: boolean;
  }) => Promise<{ show_override_modal: boolean }>;
  updateMeasureFeeAction: (data: {
    id: number;
    source: "contract" | "report" | "previous-period";
    amount: string;
    from_date: string;
    to_date: string;
    override_existing: boolean;
  }) => Promise<{ show_override_modal: boolean }>;
  getMeasureFeesAmountAction: (params: { deal_id: number; source: "contract" | "previous-period" }) => Promise<string>;
  getMeasureFeesItemAction: (id: number) => Promise<MeasureFee>;
  getDealBatchSummaryAction: (data: {
    dealId: number;
    groups: MeasureDataGroup[];
    edit_type?: "batch" | "split";
    roundsByDeals: Record<number, MeasureDataRound[]>;
  }) => Promise<{ table: MeasureDataSummaryBatchTable; message: string | null }>;
}

export class MeasureActions extends AsyncActions implements IMeasureActions {
  public constructor(params: AsyncActionsParams) {
    super(params);
  }

  private transformDataFiltersToReq = (data: Partial<MeasureDataTableFilters>): DataFiltersToReq => {
    const result: DataFiltersToReq = {};
    const {
      lastTransactions,
      calculatedFields,
      accountingDate,
      effectiveDate,
      createdAtDate,
      enteredByDate,
      hideDeleted,
      deals,
      ...rest
    } = data;

    if (typeof hideDeleted === "boolean") {
      result.hideDeleted = hideDeleted;
    }

    if (deals?.length) {
      result.deal_ids = deals.map((item) => item.id);
    }

    if (calculatedFields && calculatedFields.length) {
      // return { ...result, calculatedFields }; // Replace filters with quick filter
      result.calculatedFields = calculatedFields;
    }

    if (lastTransactions) {
      // return { ...result, lastTransactions }; // Replace filters with quick filter
      result.lastTransactions = lastTransactions;
    }

    // Transform dates
    if (accountingDate) {
      const updatedDates: DateRange<string> = {};

      for (const date in accountingDate) {
        updatedDates[date as keyof DateRange] = formatDate({
          date: accountingDate[date as keyof DateRange],
          format: "YYYY-MM-DD"
        });
      }

      result.accountingDate = updatedDates;
    }

    if (effectiveDate) {
      const updatedDates: DateRange<string> = {};

      for (const date in effectiveDate) {
        updatedDates[date as keyof DateRange] = formatDate({
          date: effectiveDate[date as keyof DateRange],
          format: "YYYY-MM-DD"
        });
      }

      result.effectiveDate = updatedDates;
    }

    if (createdAtDate) {
      const updatedDates: DateRange<string> = {};

      for (const date in createdAtDate) {
        updatedDates[date as keyof DateRange] = formatDate({
          date: createdAtDate[date as keyof DateRange],
          format: "YYYY-MM-DD"
        });
      }

      result.createdAtDate = updatedDates;
    }

    if (enteredByDate) {
      const updatedDates: DateRange<string> = {};

      for (const date in enteredByDate) {
        updatedDates[date as keyof DateRange] = formatDate({
          date: enteredByDate[date as keyof DateRange],
          format: "YYYY-MM-DD"
        });
      }

      result.updatedAtDate = updatedDates;
    }

    type RestFields = Pick<MeasureDataTableFilters, "measures" | "investors" | "enteredBy">;
    for (const key in rest) {
      const field = rest[key as keyof RestFields];
      if (Array.isArray(field)) {
        if (!field.length) {
          continue;
        }
        result[key as keyof RestFields] = field.map((item) => item.id);
      } else if (field) {
        result[key as keyof RestFields] = field;
      }
    }

    return result;
  };

  public getMeasureDataTableAction: IMeasureActions["getMeasureDataTableAction"] = async ({
    page,
    per_page = 100,
    showTopInvestors,
    filters,
    is_general = false,
    onlyDeleted,
    sort
  }) => {
    const transformedFilters = this.transformDataFiltersToReq(filters);
    const {
      data: { data: payload }
    } = await this.api.post<AltiusRes<IGetMonitoringDataRes>>({
      url: "/api/v0.0.3/deals/monitoring-data/all",
      data: {
        per_page,
        page,
        is_general: Number(is_general),
        show_top_investors: Number(showTopInvestors),
        ...(onlyDeleted !== undefined && { onlyDeleted: Number(onlyDeleted) }),
        ...transformedFilters,
        ...(sort?.order && sort.orderBy && { sort })
      }
    });
    return payload;
  };

  public getOneMeasureDataAction = async (id: number): Promise<IMeasureData> => {
    const {
      data: { data }
    } = await this.api.get<AltiusRes<IMeasureData>>({
      url: `/api/v0.0.3/deals/monitoring-data/${id}`
    });
    return data;
  };

  public deleteMeasureDataAction: IMeasureActions["deleteMeasureDataAction"] = async (
    id,
    deleteFullTransaction = false
  ) => {
    await this.api.delete({
      url: `/api/v0.0.3/deals/monitoring-data/${id}`,
      data: { delete_full_transaction: deleteFullTransaction }
    });
  };

  public updateMeasureDataAction: IMeasureActions["updateMeasureDataAction"] = async ({ id, ...body }) => {
    const formData = new FormData();

    for (const key in body) {
      if (key == "files") {
        if (body.files?.dbFiles && Object.keys(body.files.dbFiles).length > 0) {
          Object.values(body.files.dbFiles).forEach((file) => {
            formData.append("existing_file_ids[]", String(file.id));
          });
        } else {
          formData.append("existing_file_ids", "");
        }

        continue;
      }

      const value = body[key as keyof typeof body];
      if (value !== undefined) {
        formData.append(key, String(value));
        continue;
      }
    }

    if (body.files?.localFiles && Object.keys(body.files.localFiles).length > 0) {
      Object.values(body.files.localFiles).forEach((file) => {
        formData.append("new_files[]", file);
      });
    }

    formData.append("_method", "PUT");

    await this.api.post({
      url: `/api/v0.0.3/deals/monitoring-data/${id}`,
      data: formData,
      headers: {
        "Content-Type": "multipart/form-data"
      }
    });
  };

  public splitCheckAction = async (id: number): Promise<boolean> => {
    const {
      data: { data }
    } = await this.api.get<AltiusRes<{ split: boolean }>>({
      url: `/api/v0.0.3/deals/monitoring-data/${id}/split-check`
    });
    return data.split;
  };

  public getOrganizationGraphAction = async (id: number): Promise<any> => {
    const {
      data: { data }
    } = await this.api.get<AltiusRes<{ data: any }>>({
      url: "/api/v0.0.3/deals/monitoring-data/fetch-organization-graph",
      params: { deal_id: id }
    });
    return data;
  };

  // MonitoringDraft
  public saveMeasureDataDraftAction: IMeasureActions["saveMeasureDataDraftAction"] = async (data) => {
    const { deal_id, data: draftData, id } = data;

    // create files map for local files
    const filesMap: Record<string, Record<string, File>> = {};

    // process groups and replace localFiles with null
    const processedGroups = draftData.group.map((group) => {
      // process group files (by rounds)
      let processedGroupFiles: Record<
        string | number,
        { localFiles: null; dbFiles: Record<string, MonitoringDataFiles> | null }
      > | null = null;
      if (group.files) {
        processedGroupFiles = {};
        for (const [roundId, roundFiles] of Object.entries(group.files)) {
          if (roundFiles) {
            const key = `Total_${group.id}_round_${roundId}`;

            // save localFiles in filesMap
            if (roundFiles.localFiles && Object.keys(roundFiles.localFiles).length > 0) {
              filesMap[key] = roundFiles.localFiles;
            }

            // replace localFiles with null
            processedGroupFiles[roundId] = {
              ...roundFiles,
              localFiles: null
            };
          }
        }
      }

      return {
        ...group,
        files: processedGroupFiles
      };
    });

    // process measure_data and replace localFiles with null
    const processedMeasureData = draftData.measure_data.map((measureData) => {
      // find group for getting group.id
      const group = draftData.group.find((g) => g.id === measureData.group_id);
      if (!group) {
        return measureData;
      }

      const key = `group_${group.id}_key_${measureData.key}`;

      // save localFiles in filesMap
      if (measureData.files?.localFiles && Object.keys(measureData.files.localFiles).length > 0) {
        filesMap[key] = measureData.files.localFiles;
      }

      // replace localFiles with null
      return {
        ...measureData,
        files: measureData.files
          ? {
              ...measureData.files,
              localFiles: null
            }
          : null
      };
    });

    const processedData = {
      ...draftData,
      group: processedGroups,
      measure_data: processedMeasureData
    };

    const formData = new FormData();
    formData.append("deal_id", String(deal_id));
    formData.append("data", JSON.stringify(processedData));

    if (id) {
      formData.append("id", String(id));
    }

    // add files to FormData
    for (const [key, files] of Object.entries(filesMap)) {
      for (const file of Object.values(files)) {
        formData.append(`${key}[]`, file);
      }
    }

    await this.api.post({
      url: "/api/v0.0.3/deals/monitoring-data/draft",
      data: formData,
      headers: { "Content-Type": "multipart/form-data" }
    });
  };

  public getMeasureDataDraftsAction: IMeasureActions["getMeasureDataDraftsAction"] = async (deal_ids) => {
    const {
      data: { data }
    } = await this.api.get<AltiusRes<MeasureDataDraft[]>>({
      url: "/api/v0.0.3/deals/monitoring-data/draft",
      params: { deal_ids }
    });
    return data;
  };

  public deleteMeasureDataDraftAction = async (id: number): Promise<void> => {
    await this.api.delete({ url: `/api/v0.0.3/deals/monitoring-data/draft/${id}` });
  };

  public getMeasureDataDraftAction: IMeasureActions["getMeasureDataDraftAction"] = async (id) => {
    const {
      data: { data }
    } = await this.api.get<
      AltiusRes<
        {
          deal_title: string;
          deal_currency: CurrencyType;
        } & MeasureDataDraft
      >
    >({
      url: `/api/v0.0.3/deals/monitoring-data/draft/${id}`
    });
    return data;
  };

  public setMeasureDataTableFiltersAction: IMeasureActions["setMeasureDataTableFiltersAction"] = (payload) => {
    this.dispatch({ type: DealAction.SetMonitoringDataFilters, payload });
  };

  public getSplitDetailsAction: IMeasureActions["getSplitDetailsAction"] = async ({ deal_id, ...params }) => {
    const {
      data: { data }
    } = await this.api.get<AltiusRes<MeasureDataSplitDetails>>({
      url: `/api/v0.0.3/deals/${deal_id}/split-details`,
      params
    });
    return data;
  };

  public downloadDataAction: IMeasureActions["downloadDataAction"] = async ({ deal_ids, filters, hideInvestors }) => {
    const transformedFilters = this.transformDataFiltersToReq(filters);
    return this.api
      .get({
        url: "/api/v0.0.3/deals/export-data",
        params: { deal_ids, ...transformedFilters, hideInvestors },
        responseType: "blob"
      })
      .then(downloadFileAxios);
  };

  public getMeasureDataCalcAction: IMeasureActions["getMeasureDataCalcAction"] = async (params) => {
    const {
      data: { data }
    } = await this.api.get<AltiusRes<Awaited<ReturnType<IMeasureActions["getMeasureDataCalcAction"]>>>>({
      url: "/api/v0.0.3/deals/monitoring-data/calculate",
      params
    });
    return data;
  };

  public getGroupedMeasureDataCalcAction: IMeasureActions["getGroupedMeasureDataCalcAction"] = async (params) => {
    const {
      data: { data }
    } = await this.api.get<AltiusRes<Awaited<ReturnType<IMeasureActions["getGroupedMeasureDataCalcAction"]>>>>({
      url: "/api/v0.0.3/deals/monitoring-data/group-calculate",
      params
    });
    return data;
  };

  public createManyMeasureDataAction: IMeasureActions["createManyMeasureDataAction"] = async (data) => {
    const { groups, roundsByDeals } = data;

    //----added for each group existing_file_ids or key for new files ----
    const groupsForSerialization = groups.map((group) => {
      const effectiveDealId = group.sub_deal_id || group.deal_id;
      const dealRounds = roundsByDeals[effectiveDealId] || [];

      const filesByRound: Record<string, { existing_file_ids: number[]; new_file_key: string }> = Object.fromEntries(
        dealRounds.map((round) => [
          String(round.id),
          { existing_file_ids: [], new_file_key: `Total_${group.id}_round_${round.id}` }
        ])
      );

      if (group.files) {
        for (const [roundId, roundFiles] of Object.entries(group.files)) {
          if (roundFiles?.dbFiles && Object.keys(roundFiles.dbFiles).length > 0 && filesByRound[roundId]) {
            filesByRound[roundId].existing_file_ids = Object.values(roundFiles.dbFiles)
              .map((file) => file?.id)
              .filter((id) => Boolean(id));
          }
        }
      }

      return {
        ...group,
        original_measure_id: undefined,
        original_group_measure_data_ids: undefined,
        files: filesByRound,
        data: group.data.map((measureData) => ({
          ...measureData,
          files: {
            existing_file_ids:
              measureData?.files?.dbFiles && Object.keys(measureData.files.dbFiles).length > 0
                ? Object.values(measureData.files.dbFiles)
                    .map((file) => file.id)
                    .filter(Boolean)
                : [],
            new_file_key: `group_${group.id}_key_${measureData.key}`
          }
        }))
      };
    });
    //--------------------

    const formData = new FormData();
    formData.append("groups", JSON.stringify(groupsForSerialization));
    formData.append("roundsByDeals", JSON.stringify(roundsByDeals));

    for (const group of groups) {
      if (group.files) {
        for (const [roundId, roundFiles] of Object.entries(group.files)) {
          if (roundFiles?.localFiles && Object.keys(roundFiles.localFiles).length > 0) {
            const key = `Total_${group.id}_round_${roundId}`;
            for (const file of Object.values(roundFiles.localFiles)) {
              formData.append(`${key}[]`, file);
            }
          }
        }
      }

      for (const measureData of group.data) {
        const localFiles = measureData.files?.localFiles;
        if (!localFiles || Object.keys(localFiles).length === 0) continue;

        for (const file of Object.values(localFiles)) {
          const newFilesKey = `group_${group.id}_key_${measureData.key}`;
          formData.append(`${newFilesKey}[]`, file);
        }
      }
    }

    await this.api.post({
      url: "/api/v0.0.3/deals/monitoring-data",
      headers: { "Content-Type": "multipart/form-data" },
      data: formData
    });
  };

  public getMeasureParametersAction: IMeasureActions["getMeasureParametersAction"] = async () => {
    const {
      data: { data: payload }
    } = await this.api.get<AltiusRes<MeasureParameter[]>>({
      url: "/api/v0.0.3/deals/monitoring-data/parameters"
    });
    this.dispatch({ type: DealAction.GetMeasureParametersSuccess, payload });
    return payload;
  };

  public getUsedMeasuresParametersAction: IMeasureActions["getUsedMeasuresParametersAction"] = async (deal_id) => {
    const {
      data: { data: payload }
    } = await this.api.get<AltiusRes<MeasureParameter[]>>({
      url: "/api/v0.0.3/deals/monitoring-data/parameters",
      params: { deal_id }
    });
    return payload;
  };

  public getMeasuresDefinitionsListAction = async (): Promise<void> => {
    return this.api
      .get({
        url: "/api/v0.0.3/investors/list/download",
        responseType: "blob"
      })
      .then(downloadFileAxios);
  };

  public getMeasuresAction = async (): Promise<Measure[]> => {
    const {
      data: { data: payload }
    } = await this.api.get<AltiusRes<Measure[]>>({
      url: "/api/v0.0.3/measures"
    });
    this.dispatch({ type: DealAction.GetMeasuresSuccess, payload });
    return payload;
  };

  public createMeasureAction = async (data: Measure): Promise<void> => {
    const {
      status,
      data: { data: payload }
    } = await this.api.post<AltiusRes<Measure>>({
      url: "/api/v0.0.3/measures",
      data: data
    });
    if (status === 202) {
      toastify({
        message: "An measures with this name has been defined and archived, the system will reactive the measures."
      });
    }
    this.dispatch({ type: DealAction.CreateMeasureSuccess, payload });
  };

  public updateMeasureAction = async (data: UpdateMeasurePayload): Promise<void> => {
    const {
      data: { data: payload }
    } = await this.api.patch<AltiusRes<Measure>>({
      url: `/api/v0.0.3/measures/${data.id}`,
      data: data
    });
    this.dispatch({ type: DealAction.UpdateMeasureSuccess, payload });
  };

  public deleteMeasureAction = async (id: number): Promise<void> => {
    await this.api.delete({
      url: `/api/v0.0.3/measures/${id}`
    });
    this.dispatch({ type: DealAction.DeleteMeasureSuccess, payload: id });
  };

  public getMeasuresGroups: IMeasureActions["getMeasuresGroups"] = async () => {
    const {
      data: { data: payload }
    } = await this.api.get<AltiusRes<MeasureGroup[] | undefined>>({
      url: "/api/v0.0.3/measures/groups"
    });
    return payload;
  };

  public getPrimeRoles: IMeasureActions["getPrimeRoles"] = async () => {
    const {
      data: { data: payload }
    } = await this.api.get<AltiusRes<string[]>>({
      url: "/api/v0.0.3/measures/prime-roles"
    });

    return payload;
  };

  public getMeasureCalcFieldsTable: IMeasureActions["getMeasureCalcFieldsTable"] = async () => {
    const {
      data: { data: payload }
    } = await this.api.get<AltiusRes<MeasureCalcFieldsTable>>({
      url: "/api/v0.0.3/measures/measure-calc-fields-table"
    });

    return payload;
  };

  public updateMeasureCalcFieldsTable = async (data: UpdateMeasureCalcFieldsPayload): Promise<void> => {
    await this.api.patch({
      url: "/api/v0.0.3/measures/measure-calc-fields-table",
      data
    });
  };

  public updateMeasuresValuesAction: IMeasureActions["updateMeasuresValuesAction"] = async (
    data: IUpdateMeasuresValues
  ) => {
    await this.api.post({
      url: "/api/v0.0.3/measures/values",
      data: data
    });
  };

  public downloadBatchHistoryAction = async (batch_id: number): Promise<void> => {
    return this.api
      .get({
        url: "/api/v0.0.3/deals/monitoring-data/batch-history/download",
        params: { batch_id: batch_id },
        responseType: "blob"
      })
      .then(downloadFileAxios);
  };

  public getBatchTemplateAction: IMeasureActions["getBatchTemplateAction"] = async ({
    batch_id,
    template_id
  }): Promise<void> => {
    return this.api
      .get({
        url: "/api/v0.0.3/batch-template",
        params: { batch_id, template_id },
        responseType: "blob"
      })
      .then(downloadFileAxios);
  };

  public getLatestMeasureDataTableAction: IMeasureActions["getLatestMeasureDataTableAction"] = async (
    dealId: number
  ) => {
    const {
      data: { data: payload }
    } = await this.api.post<AltiusRes<ILatestMeasureData>>({
      url: "/api/v0.0.3/deals/monitoring-data/all",
      data: {
        per_page: 5,
        page: 1,
        for_deal_dashboard: true,
        deal_ids: [dealId]
      }
    });
    return payload;
  };

  public getMeasureFessTableAction: IMeasureActions["getMeasureFessTableAction"] = async (deal_id: number) => {
    const {
      data: { data }
    } = await this.api.get<AltiusRes<MeasureFeesTable>>({
      url: "/api/v0.0.3/deals/management-fees",
      params: { deal_id }
    });
    return data;
  };

  public createMeasureFeeAction: IMeasureActions["createMeasureFeeAction"] = async (body) => {
    const {
      data: { data }
    } = await this.api.post<AltiusRes<{ show_override_modal: boolean }>>({
      url: "/api/v0.0.3/deals/management-fees",
      data: body
    });
    return data;
  };

  public updateMeasureFeeAction: IMeasureActions["updateMeasureFeeAction"] = async ({ id, ...body }) => {
    const {
      data: { data }
    } = await this.api.patch<AltiusRes<{ show_override_modal: boolean }>>({
      url: `/api/v0.0.3/deals/management-fees/${id}`,
      data: body
    });
    return data;
  };

  public getMeasureFeesAmountAction: IMeasureActions["getMeasureFeesAmountAction"] = async (params) => {
    const {
      data: { data }
    } = await this.api.get<AltiusRes<{ amount: string }>>({
      url: "/api/v0.0.3/deals/management-fees/amount",
      params
    });
    return data.amount;
  };

  public getMeasureFeesItemAction: IMeasureActions["getMeasureFeesItemAction"] = async (id) => {
    const {
      data: { data }
    } = await this.api.get<AltiusRes<MeasureFee>>({
      url: `/api/v0.0.3/deals/management-fees/${id}`
    });

    return data;
  };

  public getDealBatchSummaryAction: IMeasureActions["getDealBatchSummaryAction"] = async ({ dealId, ...body }) => {
    const {
      data: { data }
    } = await this.api.post<AltiusRes<{ table: MeasureDataSummaryBatchTable; message: string | null }>>({
      url: `/api/v0.0.3/deals/${dealId}/batch-summary`,
      data: body
    });
    return data;
  };
}
